/**
 * 2048 Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 *
 * Follows SudokuErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum Game2048ErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface Game2048Error {
  type: Game2048ErrorType;
  message: string;
  details?: unknown;
  timestamp: number;
  recoverable: boolean;
}

export class Game2048ErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: Game2048Error[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: Game2048ErrorType,
    message: string,
    details?: unknown,
    showNotification: boolean = true
  ): Game2048Error {
    const error: Game2048Error = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: '2048', additionalData: details as Record<string, unknown> },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: Game2048ErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case Game2048ErrorType.INVALID_MOVE:
      case Game2048ErrorType.GAME_ENDED:
      case Game2048ErrorType.GAME_LOGIC:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case Game2048ErrorType.CORRUPTED_DATA:
      case Game2048ErrorType.SAVE_ERROR:
      case Game2048ErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  handleGameLogicError(reason: string): Game2048Error {
    return this.handleError(
      Game2048ErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleCorruptedData(dataType: string, error: unknown): Game2048Error {
    return this.handleError(
      Game2048ErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: unknown): Game2048Error {
    return this.handleError(
      Game2048ErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: unknown): Game2048Error {
    return this.handleError(
      Game2048ErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  private isRecoverable(type: Game2048ErrorType): boolean {
    switch (type) {
      case Game2048ErrorType.INVALID_MOVE:
      case Game2048ErrorType.GAME_ENDED:
      case Game2048ErrorType.GAME_LOGIC:
      case Game2048ErrorType.CORRUPTED_DATA:
      case Game2048ErrorType.SAVE_ERROR:
      case Game2048ErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: Game2048Error): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: Game2048Error): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: '2048 Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): Game2048Error[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
